import { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { JoinTheWaitlist } from 'features/misc';

// --- Cursor, Background, and Particle Logic (unchanged) ---
function useWavyCursor(strength = 30, waveStrength = 20, speed = 0.002) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const timeRef = useRef(0);

  useEffect(() => {
    const handleMove = (e) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const moveX = (e.clientX - centerX) / centerX;
      const moveY = (e.clientY - centerY) / centerY;

      timeRef.current += 1;

      const waveX = Math.sin(timeRef.current * speed) * waveStrength;
      const waveY = Math.cos(timeRef.current * speed * 1.5) * waveStrength;

      setOffset({ x: moveX * strength + waveX, y: moveY * strength + waveY });
    };

    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [strength, waveStrength, speed]);

  return offset;
}

function FloatingParticles({ offset }) {
  const particles = Array.from({ length: 12 }).map((_, i) => ({
    id: i,
    size: Math.random() * 50 + 30,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 5,
    duration: 25 + Math.random() * 10,
  }));

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: 0, opacity: 0.3 }}
          animate={{ y: ['-5%', '5%', '-5%'], opacity: [0.2, 0.4, 0.2] }}
          transition={{ repeat: Infinity, duration: p.duration, ease: 'easeInOut', delay: p.delay }}
          style={{
            position: 'absolute',
            top: p.top,
            left: p.left,
            width: p.size,
            height: p.size,
            background: 'radial-gradient(circle, rgba(247,147,30,0.25), transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(20px)',
            transform: `translate(${offset.x * 0.2}px, ${offset.y * 0.2}px)`,
          }}
        />
      ))}
    </div>
  );
}

function EnergyGrid({ offset }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none z-0 opacity-10"
      style={{
        transform: `translate(${offset.x * 0.1}px, ${offset.y * 0.1}px)`,
        transition: 'transform 0.2s ease-out',
      }}
    >
      <div className="absolute w-full h-full bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[length:40px_40px] animate-[moveGridX_30s_linear_infinite]" />
      <div className="absolute w-full h-full bg-[linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[length:40px_40px] animate-[moveGridY_40s_linear_infinite]" />
      <style jsx>{`
        @keyframes moveGridX {
          0% {
            background-position-x: 0;
          }
          100% {
            background-position-x: 40px;
          }
        }
        @keyframes moveGridY {
          0% {
            background-position-y: 0;
          }
          100% {
            background-position-y: 40px;
          }
        }
      `}</style>
    </div>
  );
}

function PulsingTriangleCursor({ loading = false }) {
  const cursorRef = useRef(null);
  const [trail, setTrail] = useState([]);
  const [hovering, setHovering] = useState(false);
  const waveTime = useRef(0);

  useEffect(() => {
    const handleMove = (e) => {
      const x = e.clientX;
      const y = e.clientY;

      if (cursorRef.current) {
        cursorRef.current.style.left = `${x}px`;
        cursorRef.current.style.top = `${y}px`;
        cursorRef.current.style.transform = `translate(-50%, -50%) rotate(-45deg)`;
      }

      waveTime.current += 0.2;
      setTrail((prev) => [
        { x, y, id: Date.now(), waveOffset: waveTime.current },
        ...prev.filter((p) => Date.now() - p.id < 500),
      ]);
    };

    const handleHover = (e) => {
      setHovering(!!e.target.closest('a, button'));
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseover', handleHover);
    document.body.style.cursor = 'none';

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseover', handleHover);
      document.body.style.cursor = 'auto';
    };
  }, []);

  const glowColor = hovering ? 'rgba(0,200,255,1)' : 'rgba(247,147,30,1)';
  const trailColor = hovering ? 'rgba(0,200,255,0.7)' : 'rgba(247,147,30,0.7)';

  return (
    <>
      <div
        ref={cursorRef}
        className="fixed z-50 w-6 h-6 pointer-events-none"
        style={{
          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
          border: `2px solid ${glowColor}`,
          background: 'transparent',
          boxShadow: `0 0 15px ${glowColor}, 0 0 25px ${glowColor}`,
          animation: `${loading ? 'spinCursor 1s linear infinite' : 'pulseOutline 1.5s infinite'}`,
          filter: 'blur(0.5px)',
        }}
      />
      {trail.map((p) => {
        const waveY = Math.sin((Date.now() / 80 + p.waveOffset) * 2) * 5;
        return (
          <div
            key={p.id}
            className="fixed pointer-events-none"
            style={{
              left: p.x,
              top: p.y + waveY,
              width: '6px',
              height: '6px',
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%',
              border: `1px solid ${trailColor}`,
              boxShadow: `0 0 10px ${trailColor}`,
              background: 'transparent',
              animation: 'fadeWavyTrail 0.5s forwards',
            }}
          />
        );
      })}
      <style jsx>{`
        @keyframes pulseOutline {
          0%,
          100% {
            transform: scale(1) translate(-50%, -50%) rotate(-45deg);
            opacity: 1;
          }
          50% {
            transform: scale(1.3) translate(-50%, -50%) rotate(-45deg);
            opacity: 0.6;
          }
        }
        @keyframes spinCursor {
          0% {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          100% {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }
        @keyframes fadeWavyTrail {
          0% {
            opacity: 1;
            transform: scale(1) translate(-50%, -50%);
          }
          100% {
            opacity: 0;
            transform: scale(0.5) translate(-50%, -50%);
          }
        }
      `}</style>
    </>
  );
}

export function WebLayout({ headerContent, children }) {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 600], [0, 50]);
  const offset = useWavyCursor(30, 20, 0.03);

  return (
    <div className="relative flex flex-col min-h-screen bg-[#050505] text-white overflow-hidden">
      <FloatingParticles offset={offset} />
      <EnergyGrid offset={offset} />
      <motion.div
        style={{ y: bgY, transform: `translate(${offset.x * 0.3}px, ${offset.y * 0.3}px)` }}
        className="fixed inset-0 z-0 pointer-events-none"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e0e0e] via-[#1a1a1a] to-[#0e0e0e] opacity-80" />
      </motion.div>

      <PulsingTriangleCursor loading={false} />
      <Navbar />

      <div className="relative z-10">{headerContent}</div>
      <main className="relative z-10 flex-grow px-4 sm:px-24 py-12">{children}</main>
      <JoinTheWaitlist />
      <Footer />
    </div>
  );
}
